import { BaseSurvey } from "@/redux/survey/types";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import { DownloadResponse } from "@/shared/services/types";
import {
  AuditResponse,
  BaseData,
  BasePaginationQueryParams,
  BaseUUIDData,
} from "@/redux/types";

type SurveyViewTags = "SurveyView" | "SurveyViews";
type DataBlockReferencesTags = "DataBlockReferences";
export type Tags = SurveyViewTags | DataBlockReferencesTags;

export type TagsType =
  | { type: "SurveyView" }
  | { type: "SurveyView"; id: string }
  | { type: "SurveyViews" }
  | { type: "DataBlockReferences" };

export interface SurveyViewQueryParams extends BasePaginationQueryParams {
  title?: string;
  survey?: number;
  dataFormat?: string;
  distributionFormat?: string;
  organization?: number;
  publicationDestination?: string | string[] | null;
  organizations?: number[];
  countries?: [];
  chapters?: [];
}

export enum SurveyViewDataFormat {
  RAW = "RAW",
  DESCRIPTIVE = "DESCRIPTIVE",
}

export enum SurveyViewDistributionFormat {
  DIGITAL = "DIGITAL",
  PRINT = "PRINT",
}

export enum SurveyViewColorScheme {
  DEFAULT = "DEFAULT",
  GRAY_SCALE = "GRAY_SCALE",
}

export enum SurveyViewPublicationDestination {
  EXTERNAL = "EXTERNAL",
  IN_APP = "IN_APP",
  WEBSITE = "WEBSITE",
}

export interface SurveyViewConfig extends BaseData {
  surveyView: number;
  colorScheme: SurveyViewColorScheme;
  dataFormat: SurveyViewDataFormat;
  distributionFormat: SurveyViewDistributionFormat;
  publicationDestination: SurveyViewPublicationDestination;
  includeFrontCover: boolean;
  includeBackCover: boolean;
  includeTableOfContent: boolean;
  includePrmePage: boolean;
  includeSdgPage: boolean;
  includeOrganizationSummary: boolean;
  includeContributors: boolean;
  includeDividers: boolean;
  coverPageBackgroundColor: string;
}

export interface SurveyViewEntity {
  id: number;
  sharingRights: string;
}

export interface SurveyViewAnswer extends BaseData {
  answer: number;
  question: number;
  attachedBlocks: SurveyViewEntity[];
}

export interface BaseSurveyViewResponse extends BaseUUIDData, AuditResponse {
  title: string;
  description: string;
  survey: number;
  sharingRights: string;
  views: number;
  config: SurveyViewConfig;
  organization: BaseOrganizationResponse;
}

export interface SurveyViewResponse extends BaseSurveyViewResponse {
  answers: SurveyViewAnswer[];
  createdAt: string;
}

export interface SurveyViewWithSurvey
  extends Omit<BaseSurveyViewResponse, "survey"> {
  survey: BaseSurvey;
}

export interface SurveyViewPreview {
  htmlData: string;
}

export type SurveyViewDownload = DownloadResponse;

export interface SurveyViewConfigData {
  includeFrontCover: boolean;
  includeBackCover: boolean;
  includeTableOfContent: boolean;
  includePrmePage: boolean;
  includeSdgPage: boolean;
  includeOrganizationSummary: boolean;
  includeContributors: boolean;
  includeDividers: boolean;
  publicationDestination: string;
  coverPageBackgroundColor: string;
  colorScheme: SurveyViewColorScheme;
  dataFormat: keyof typeof SurveyViewDataFormat;
  distributionFormat: SurveyViewDistributionFormat;
}

export interface CreateUpdateSurveyView {
  survey: number;
  title: string;
  description?: string;
  config: SurveyViewConfigData;
  answers: {
    answer: number;
    attachedBlocks: number[];
  }[];
}

export type PreviewSurveyViewRequest = CreateUpdateSurveyView;
